import { useCallback, useEffect, useMemo, useState } from 'react';
import { addMoney, money, moneyFromMajorUnits, sumMoney, zero } from '@loctopus/ticketing-sdk';
import type {
  AddOn,
  AddOnSelectionInput,
  CheckoutLineItemInput,
  CustomQuestion,
  EventDetail,
  EventSession,
  PromoCodeValidationResult,
  SeatMap,
  SeatMapSeat,
  TicketType,
} from '@loctopus/ticketing-sdk';
import type { ParsedApiError } from '@loctopus/shared';
// `multiplyMoney` isn't re-exported by @loctopus/ticketing-sdk (packages/sdk/src/index.ts only
// re-exports a subset) - imported directly from @loctopus/shared instead, same as the existing
// `ParsedApiError` import just above (apps/checkout is the core app, not a clients/{slug} custom
// build, so it isn't bound by docs/CUSTOM_CLIENT_BUILDS.md's "sdk + ui only" rule).
import { multiplyMoney } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { useAppContext } from '../lib/AppContext';
import { useRouter } from '../router';
import {
  Button,
  Card,
  ErrorBanner,
  FormField,
  Input,
  Spinner,
} from '../components/ui';
import { Turnstile } from '../components/Turnstile';
import { SeatMapPicker } from '../components/SeatMapPicker';
import { AboutContent } from '../components/AboutContent';
import { toParsedError } from '../lib/errorDisplay';
import { formatDateRange, formatMoney, isFreeMoney } from '../lib/format';
import { fetchRefundPolicy } from '../lib/refundPolicy';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; error: ParsedApiError }
  | { status: 'ready'; event: EventDetail };

/**
 * `TicketType` (packages/sdk/src/types.ts, a shared package apps/checkout does not own) has no
 * fee field yet. Per this build's brief, the backend's fee-engine work is expected to add a
 * per-ticket-type buyer-paid booking fee to `GET /v1/events/:id`'s `ticketTypes[]` - assumed named
 * `buyerFeeMinor` (flat minor-units integer, same currency as `price`, same naming convention as
 * the raw API's existing `priceMinor` - see packages/sdk/src/normalizeEvent.ts's `RawEventResponse`).
 * Flag to the backend/fee-engine agent if the real field name differs.
 *
 * IMPORTANT (reported, not fixed - outside apps/checkout's ownership): even once the backend adds
 * this field, `packages/sdk/src/normalizeEvent.ts`'s `normalizeEventDetail` will keep stripping it
 * out before this app ever sees it - that function builds a brand-new ticket-type object listing
 * only `id/eventId/sessionId/name/kind/price/capacity/perOrderLimit/saleStartsAt/saleEndsAt/
 * isHidden` and does not pass through unknown raw fields. `normalizeEventDetail` needs a matching
 * `buyerFeeMinor` line added before this ever renders real fee data - confirmed live against the
 * real seeded event (`storefront-v2-qa-night` / "General Admission", $25.00 AUD, no fee field
 * present today), not fixed here per "report bugs, don't fix them".
 */
interface TicketTypeWithFee extends TicketType {
  buyerFeeMinor?: number;
}

/**
 * Same situation as `buyerFeeMinor` above: `EventDetail` (packages/sdk/src/types.ts) has no
 * "about this event" field yet. Assumed named `aboutContent` (plain text, optionally with
 * lightweight `#`/`##`/`-` markdown-ish structure) on the event object itself. Also stripped by
 * `normalizeEventDetail` today for the same reason - reported, not fixed.
 */
interface EventDetailWithAbout extends EventDetail {
  aboutContent?: string | null;
}

/**
 * Fee-inclusive pricing for the VERY FIRST screen a buyer sees for a ticket type (this build's
 * task #1). Free tickets have nothing to add a fee to. `buyerFeeMinor` defaults to 0 when absent
 * (see `TicketTypeWithFee` above) so this renders a correct $0.00-fee breakdown today and will
 * pick up the real fee the moment both the backend and `packages/sdk` land it - never silently
 * fakes a non-zero fee.
 */
function getFeeInclusiveBreakdown(
  ticketType: TicketType,
): { ticketPrice: ReturnType<typeof money>; fee: ReturnType<typeof money>; total: ReturnType<typeof money> } | null {
  if (isFreeMoney(ticketType.price)) return null;
  const feeMinor = (ticketType as TicketTypeWithFee).buyerFeeMinor ?? 0;
  const fee = money(feeMinor, ticketType.price.currency);
  const total = addMoney(ticketType.price, fee);
  return { ticketPrice: ticketType.price, fee, total };
}

export function EventPage({ eventId }: { eventId: string }) {
  const { client, config } = useAppContext();
  const { navigate } = useRouter();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [session, setSession] = useState<EventSession | null>(null);
  const [seatMap, setSeatMap] = useState<SeatMap | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [selectedSeatIds, setSelectedSeatIds] = useState<Set<string>>(new Set());
  const [addOnQuantities, setAddOnQuantities] = useState<Record<string, number>>({});
  const [donationAmounts, setDonationAmounts] = useState<Record<string, string>>({});
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState<PromoCodeValidationResult | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [checkoutType, setCheckoutType] = useState<'guest' | 'account'>('guest');
  const [buyer, setBuyer] = useState({ email: '', firstName: '', lastName: '', phone: '' });
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [refundPolicyText, setRefundPolicyText] = useState<string | null>(null);
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  // Local, already-specific client-side validation messages (no ticket chosen, missing email,
  // etc) - distinct from a real API failure, which carries a request ID and structured detail.
  const [clientValidationError, setClientValidationError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<ParsedApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    client
      .getEvent(eventId)
      .then((event) => {
        if (cancelled) return;
        setState({ status: 'ready', event });
        setSession(event.sessions[0] ?? null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ status: 'error', error: toParsedError(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client, eventId]);

  const hasReservedSeating =
    state.status === 'ready' && state.event.ticketTypes.some((tt) => tt.kind === 'reserved');

  useEffect(() => {
    if (!hasReservedSeating || state.status !== 'ready') return;
    let cancelled = false;
    client
      .getSeatMap(eventId, { sessionId: session?.id })
      .then((map) => {
        if (!cancelled) setSeatMap(map);
      })
      .catch(() => {
        if (!cancelled) setSeatMap(null);
      });
    return () => {
      cancelled = true;
    };
  }, [client, eventId, session?.id, hasReservedSeating, state.status]);

  const event = state.status === 'ready' ? state.event : null;
  const aboutContent = event ? (event as EventDetailWithAbout).aboutContent : undefined;

  // Refund policy at checkout: non-critical read - never blocks the buyer from checking out if
  // it fails (see `fetchRefundPolicy`'s doc comment - this tenant's policy, resolved server-side
  // from the request itself, same as every other public read on this Worker).
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    fetchRefundPolicy(config, controller.signal)
      .then((text) => {
        if (!cancelled) setRefundPolicyText(text);
      })
      .catch(() => {
        if (!cancelled) setRefundPolicyText(null);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [config]);

  const ticketTypes = useMemo(
    () =>
      event
        ? event.ticketTypes.filter(
            (tt) => !tt.isHidden && (tt.sessionId == null || tt.sessionId === session?.id),
          )
        : [],
    [event, session?.id],
  );
  const generalTicketTypes = useMemo(
    () => ticketTypes.filter((tt) => tt.kind !== 'reserved'),
    [ticketTypes],
  );
  const reservedTicketTypesById = useMemo(
    () => new Map(ticketTypes.filter((tt) => tt.kind === 'reserved').map((tt) => [tt.id, tt])),
    [ticketTypes],
  );

  const seatsByTicketType = useMemo(() => {
    const map = new Map<string, SeatMapSeat[]>();
    if (!seatMap) return map;
    for (const section of seatMap.sections) {
      for (const seat of section.seats) {
        if (!selectedSeatIds.has(seat.id)) continue;
        const key = seat.ticketTypeId ?? '';
        map.set(key, [...(map.get(key) ?? []), seat]);
      }
    }
    return map;
  }, [seatMap, selectedSeatIds]);

  const lineItems: CheckoutLineItemInput[] = useMemo(() => {
    const items: CheckoutLineItemInput[] = [];
    for (const tt of generalTicketTypes) {
      const qty = quantities[tt.id] ?? 0;
      if (qty > 0) items.push({ ticketTypeId: tt.id, quantity: qty });
    }
    for (const [ticketTypeId, seats] of seatsByTicketType) {
      if (ticketTypeId)
        items.push({ ticketTypeId, quantity: seats.length, seatIds: seats.map((s) => s.id) });
    }
    return items;
  }, [generalTicketTypes, quantities, seatsByTicketType]);

  const totalTicketCount = lineItems.reduce((sum, item) => sum + item.quantity, 0);

  const ticketTypesById = useMemo(
    () => new Map(ticketTypes.map((tt) => [tt.id, tt])),
    [ticketTypes],
  );

  // REAL BUG FOUND AND FIXED: the sticky footer only ever showed a ticket COUNT, never a dollar
  // amount - confirmed live, clicking "+1" never changed any total anywhere on the page. This is
  // a CLIENT-SIDE ESTIMATE only (ticket price + per-ticket booking fee, add-ons, and the promo
  // discount applied the same way the backend does - see packages/billing/src/fees.ts and this
  // app's own `getFeeInclusiveBreakdown` above); the real, authoritative total is always the one
  // `createCheckoutSession` returns and the next page (CheckoutPage) shows before payment.
  const estimatedTotal = useMemo(() => {
    const currency = event?.currency ?? 'AUD';
    const ticketLines = lineItems.map((item) => {
      const tt = ticketTypesById.get(item.ticketTypeId);
      if (!tt) return zero(currency);
      const unitTotal = addMoney(tt.price, money(tt.buyerFeeMinor ?? 0, tt.price.currency));
      return money(unitTotal.amountMinor * item.quantity, unitTotal.currency);
    });
    const ticketSubtotal = sumMoney(
      lineItems.map((item) => {
        const tt = ticketTypesById.get(item.ticketTypeId);
        return tt ? money(tt.price.amountMinor * item.quantity, tt.price.currency) : zero(currency);
      }),
      currency,
    );

    const addOnLines: ReturnType<typeof money>[] = [];
    for (const a of event?.addOns ?? []) {
      if (a.kind === 'donation') {
        const majorUnits = donationAmounts[a.id];
        if (majorUnits && Number(majorUnits) > 0) {
          addOnLines.push(moneyFromMajorUnits(majorUnits, currency));
        }
        continue;
      }
      const quantity = addOnQuantities[a.id] ?? 0;
      if (quantity > 0 && a.price) {
        addOnLines.push(money(a.price.amountMinor * quantity, a.price.currency));
      }
    }

    let discount = zero(currency);
    if (promoResult?.valid && promoResult.discount) {
      if (promoResult.discount.kind === 'percent') {
        discount = multiplyMoney(ticketSubtotal, promoResult.discount.percentBps / 10000);
      } else if (promoResult.discount.kind === 'fixed') {
        discount = money(
          Math.min(promoResult.discount.amount.amountMinor, ticketSubtotal.amountMinor),
          currency,
        );
      }
    }

    const total = sumMoney([...ticketLines, ...addOnLines], currency);
    return money(Math.max(0, total.amountMinor - discount.amountMinor), currency);
  }, [event, lineItems, ticketTypesById, addOnQuantities, donationAmounts, promoResult]);

  const setQuantity = useCallback((ticketTypeId: string, delta: number, max?: number | null) => {
    setQuantities((prev) => {
      const next = Math.max(0, (prev[ticketTypeId] ?? 0) + delta);
      const capped = max != null ? Math.min(next, max) : next;
      return { ...prev, [ticketTypeId]: capped };
    });
  }, []);

  const toggleSeat = useCallback((seat: SeatMapSeat) => {
    setSelectedSeatIds((prev) => {
      const next = new Set(prev);
      if (next.has(seat.id)) next.delete(seat.id);
      else next.add(seat.id);
      return next;
    });
  }, []);

  const maxSeats = useMemo(
    () =>
      [...reservedTicketTypesById.values()].reduce(
        (sum, tt) => sum + (tt.perOrderLimit ?? 20),
        0,
      ) || 20,
    [reservedTicketTypesById],
  );

  async function runPromoValidation() {
    setPromoError(null);
    setPromoResult(null);
    if (!promoCode.trim()) return;
    try {
      const result = await client.validatePromoCode({ eventId, code: promoCode.trim() });
      setPromoResult(result);
      if (!result.valid)
        setPromoError(result.message ?? `"${promoCode}" is not a valid code for this event.`);
    } catch (err) {
      setPromoError(toParsedError(err).message);
    }
  }

  async function submit() {
    if (!event) return;
    setClientValidationError(null);
    setSubmitError(null);
    if (totalTicketCount === 0) {
      setClientValidationError('Choose at least one ticket to continue.');
      return;
    }
    if (!buyer.email.trim()) {
      setClientValidationError('Enter an email address so we know where to send your tickets.');
      return;
    }
    const missingRequired = event.customQuestions.find((q) => q.required && !answers[q.id]?.trim());
    if (missingRequired) {
      setClientValidationError(`Please answer "${missingRequired.label}".`);
      return;
    }
    if (refundPolicyText && !agreedToPolicy) {
      setClientValidationError('Please agree to the refund policy and terms before continuing.');
      return;
    }
    if (!turnstileToken) {
      setClientValidationError('Please complete the verification check below before continuing.');
      return;
    }

    setSubmitting(true);
    try {
      const checkoutSession = await client.createCheckoutSession({
        eventId,
        sessionId: session?.id,
        lineItems,
        addOns: event.addOns.reduce<AddOnSelectionInput[]>((acc, a) => {
          if (a.kind === 'donation') {
            const majorUnits = donationAmounts[a.id];
            if (majorUnits && Number(majorUnits) > 0) {
              acc.push({
                addOnId: a.id,
                quantity: 1,
                amount: moneyFromMajorUnits(majorUnits, event.currency),
              });
            }
            return acc;
          }
          const quantity = addOnQuantities[a.id] ?? 0;
          if (quantity > 0) acc.push({ addOnId: a.id, quantity });
          return acc;
        }, []),
        promoCode: promoResult?.valid ? promoCode.trim() : undefined,
        customQuestionAnswers: event.customQuestions
          .map((q) => ({ questionId: q.id, answer: answers[q.id]?.trim() ?? '' }))
          .filter((a): a is { questionId: string; answer: string } => a.answer.length > 0),
        buyer: {
          email: buyer.email.trim(),
          firstName: buyer.firstName.trim() || undefined,
          lastName: buyer.lastName.trim() || undefined,
          phone: buyer.phone.trim() || undefined,
        },
        turnstileToken,
      });
      navigate({
        name: 'checkout',
        checkoutSessionId: checkoutSession.id,
        accessToken: checkoutSession.accessToken,
      });
    } catch (err) {
      setSubmitError(toParsedError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (state.status === 'loading') {
    return <Spinner label="Loading event…" />;
  }
  if (state.status === 'error') {
    return <ErrorSummary error={state.error} />;
  }
  if (!event) {
    return null;
  }

  // Real photo for this specific event, if the tenant's runtime config supplies one (stopgap
  // until a real cover-image upload feature exists - see theme/types.ts's `eventHeroImages` doc
  // comment). `event.coverImageUrl` is preferred since it's the real, generic SDK field - nothing
  // populates it anywhere today, so this falls back to the per-tenant config map.
  const heroImageUrl = event.coverImageUrl ?? config.eventHeroImages?.[event.id];

  // Previously the event's date/time was shown ONLY when there was more than one session
  // (`event.sessions.length > 1`, the "Choose a date" card below) - a single-session event like
  // most real shows never displayed when it actually was, anywhere on this page. Always shown now.
  const primarySessionWhen = session
    ? formatDateRange(session.startsAt, session.endsAt, event.timezone)
    : null;

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate({ name: 'events' })}
        className="text-sm text-brand-primary mb-4 focus-ring"
      >
        ← All events
      </button>

      {heroImageUrl && (
        <img
          src={heroImageUrl}
          alt={event.title}
          className="w-full h-48 sm:h-64 object-cover rounded-brand mb-4"
        />
      )}

      <h1 className="font-heading text-2xl font-semibold text-slate-800 mb-1">{event.title}</h1>

      <dl className="flex flex-col gap-1 text-sm text-slate-600 mb-6">
        {primarySessionWhen && (
          <div className="flex gap-2">
            <dt className="font-medium text-slate-700">When:</dt>
            <dd>{primarySessionWhen}</dd>
          </div>
        )}
        {(event.venueName || event.venueAddress) && (
          <div className="flex gap-2">
            <dt className="font-medium text-slate-700">Where:</dt>
            <dd>
              {[event.venueName, event.venueAddress].filter(Boolean).join(', ')}
            </dd>
          </div>
        )}
        <div className="flex gap-2">
          <dt className="font-medium text-slate-700">Presented by:</dt>
          <dd>{config.theme.displayName}</dd>
        </div>
      </dl>

      {aboutContent && (
        <Card title="About this event" className="mb-4">
          <AboutContent text={aboutContent} />
        </Card>
      )}

      {event.sessions.length > 1 && (
        <Card title="Choose a date" className="mb-4">
          <div className="flex flex-wrap gap-2">
            {event.sessions.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  setSession(s);
                  setSelectedSeatIds(new Set());
                }}
                aria-pressed={session?.id === s.id}
                className={`px-3 py-2 text-sm rounded-brand border focus-ring ${
                  session?.id === s.id
                    ? 'bg-brand-primary text-white border-brand-primary'
                    : 'bg-white border-slate-300 hover:bg-slate-50'
                }`}
              >
                {new Date(s.startsAt).toLocaleString('en-AU', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </button>
            ))}
          </div>
        </Card>
      )}

      {generalTicketTypes.length > 0 && (
        <Card title="Tickets" className="mb-4">
          <ul className="space-y-3">
            {generalTicketTypes.map((tt) => (
              <TicketTypeRow
                key={tt.id}
                ticketType={tt}
                quantity={quantities[tt.id] ?? 0}
                onChange={setQuantity}
              />
            ))}
          </ul>
        </Card>
      )}

      {hasReservedSeating && (
        <Card title="Select your seats" className="mb-4">
          {seatMap ? (
            <SeatMapPicker
              seatMap={seatMap}
              selectedSeatIds={selectedSeatIds}
              maxSelectable={maxSeats}
              onToggleSeat={toggleSeat}
            />
          ) : (
            <Spinner label="Loading seat map…" />
          )}
        </Card>
      )}

      {event.addOns.length > 0 && (
        <Card title="Add-ons" className="mb-4">
          <ul className="space-y-3">
            {event.addOns.map((addOn) =>
              addOn.kind === 'donation' ? (
                <DonationRow
                  key={addOn.id}
                  addOn={addOn}
                  value={donationAmounts[addOn.id] ?? ''}
                  onChange={(value) =>
                    setDonationAmounts((prev) => ({ ...prev, [addOn.id]: value }))
                  }
                />
              ) : (
                <AddOnRow
                  key={addOn.id}
                  addOn={addOn}
                  quantity={addOnQuantities[addOn.id] ?? 0}
                  onChange={(delta) =>
                    setAddOnQuantities((prev) => ({
                      ...prev,
                      [addOn.id]: Math.max(0, (prev[addOn.id] ?? 0) + delta),
                    }))
                  }
                />
              ),
            )}
          </ul>
        </Card>
      )}

      {event.customQuestions.length > 0 && (
        <Card title="A few questions" className="mb-4">
          {event.customQuestions.map((q) => (
            <CustomQuestionField
              key={q.id}
              question={q}
              value={answers[q.id] ?? ''}
              onChange={(value) => setAnswers((prev) => ({ ...prev, [q.id]: value }))}
            />
          ))}
        </Card>
      )}

      <Card title="Promo code" className="mb-4">
        <div className="flex gap-2">
          <Input
            aria-label="Promo code"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            placeholder="Enter code"
          />
          <Button type="button" variant="secondary" onClick={runPromoValidation}>
            Apply
          </Button>
        </div>
        {promoError && (
          <p role="alert" className="mt-2 text-sm text-red-600">
            {promoError}
          </p>
        )}
        {promoResult?.valid && <p className="mt-2 text-sm text-emerald-700">Code applied.</p>}
      </Card>

      <Card title="Your details" className="mb-4">
        <label className="flex items-center gap-2 mb-4 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={checkoutType === 'account'}
            onChange={(e) => setCheckoutType(e.target.checked ? 'account' : 'guest')}
            className="h-4 w-4 rounded border-slate-300 text-brand-primary focus-ring"
          />
          Save my details to receive future discount promo codes
        </label>
        <FormField label="Email" htmlFor="buyer-email" required>
          <Input
            id="buyer-email"
            type="email"
            autoComplete="email"
            value={buyer.email}
            onChange={(e) => setBuyer((b) => ({ ...b, email: e.target.value }))}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="First name" htmlFor="buyer-first">
            <Input
              id="buyer-first"
              autoComplete="given-name"
              value={buyer.firstName}
              onChange={(e) => setBuyer((b) => ({ ...b, firstName: e.target.value }))}
            />
          </FormField>
          <FormField label="Last name" htmlFor="buyer-last">
            <Input
              id="buyer-last"
              autoComplete="family-name"
              value={buyer.lastName}
              onChange={(e) => setBuyer((b) => ({ ...b, lastName: e.target.value }))}
            />
          </FormField>
        </div>
        <FormField
          label="Phone"
          htmlFor="buyer-phone"
          hint="Optional - used for event-day updates only."
        >
          <Input
            id="buyer-phone"
            type="tel"
            autoComplete="tel"
            value={buyer.phone}
            onChange={(e) => setBuyer((b) => ({ ...b, phone: e.target.value }))}
          />
        </FormField>
      </Card>

      {refundPolicyText && (
        <Card title="Refund policy" className="mb-4">
          <p className="text-sm text-slate-600 whitespace-pre-line mb-3">{refundPolicyText}</p>
          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={agreedToPolicy}
              onChange={(e) => setAgreedToPolicy(e.target.checked)}
              className="h-4 w-4 mt-0.5 rounded border-slate-300 text-brand-primary focus-ring"
            />
            I agree to the refund policy and the terms and conditions
          </label>
        </Card>
      )}

      <Card className="mb-4">
        <Turnstile
          siteKey={config.turnstileSiteKey}
          onToken={setTurnstileToken}
          onExpire={() => setTurnstileToken(null)}
        />
      </Card>

      {clientValidationError && <ErrorBanner message={clientValidationError} />}
      {submitError && <ErrorSummary error={submitError} className="mt-2" />}

      <div className="flex items-center justify-between sticky bottom-0 bg-brand-bg py-3 border-t border-slate-200">
        <div>
          <p className="text-sm text-slate-600">{totalTicketCount} ticket(s) selected</p>
          {totalTicketCount > 0 && (
            <p className="text-lg font-semibold text-slate-800">
              {formatMoney(estimatedTotal)}
            </p>
          )}
        </div>
        <Button
          type="button"
          size="lg"
          disabled={submitting || (!!refundPolicyText && !agreedToPolicy)}
          onClick={submit}
        >
          {submitting ? 'Starting checkout…' : 'Continue to payment'}
        </Button>
      </div>
    </div>
  );
}

function TicketTypeRow({
  ticketType,
  quantity,
  onChange,
}: {
  ticketType: TicketType;
  quantity: number;
  onChange: (id: string, delta: number, max?: number | null) => void;
}) {
  const breakdown = getFeeInclusiveBreakdown(ticketType);
  return (
    <li className="flex items-center justify-between gap-4" data-testid="ticket-type-row">
      <div>
        <p className="font-medium text-slate-800">{ticketType.name}</p>
        {breakdown ? (
          <div data-testid="ticket-price">
            {/* Headline (task #1): the fee-inclusive TOTAL is the first and most prominent price
             * shown for this ticket type - never a bare ticket price with the fee appearing only
             * later in a breakdown. */}
            <p className="text-sm font-semibold text-slate-700">
              {formatMoney(breakdown.total)}{' '}
              <span className="font-normal text-slate-500">incl. booking fee</span>
            </p>
            {/* Breakdown shown at least as prominently, directly below the headline. */}
            <p className="text-xs text-slate-400" data-testid="ticket-price-breakdown">
              {formatMoney(breakdown.ticketPrice)} ticket + {formatMoney(breakdown.fee)} booking
              fee
            </p>
          </div>
        ) : (
          <p className="text-sm text-slate-500" data-testid="ticket-price">
            Free
          </p>
        )}
      </div>
      <QuantityStepper
        value={quantity}
        onDecrement={() => onChange(ticketType.id, -1, ticketType.perOrderLimit)}
        onIncrement={() => onChange(ticketType.id, 1, ticketType.perOrderLimit)}
        label={ticketType.name}
      />
    </li>
  );
}

function AddOnRow({
  addOn,
  quantity,
  onChange,
}: {
  addOn: AddOn;
  quantity: number;
  onChange: (delta: number) => void;
}) {
  return (
    <li className="flex items-center justify-between gap-4">
      <div>
        <p className="font-medium text-slate-800">{addOn.name}</p>
        <p className="text-sm text-slate-500">{addOn.price ? formatMoney(addOn.price) : ''}</p>
      </div>
      <QuantityStepper
        value={quantity}
        onDecrement={() => onChange(-1)}
        onIncrement={() => onChange(1)}
        label={addOn.name}
      />
    </li>
  );
}

function DonationRow({
  addOn,
  value,
  onChange,
}: {
  addOn: AddOn;
  value: string;
  onChange: (value: string) => void;
}) {
  const fieldId = `donation-${addOn.id}`;
  return (
    <li>
      <FormField label={addOn.name} htmlFor={fieldId} hint="Enter any amount.">
        <Input
          id={fieldId}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </FormField>
    </li>
  );
}

function QuantityStepper({
  value,
  onDecrement,
  onIncrement,
  label,
}: {
  value: number;
  onDecrement: () => void;
  onIncrement: () => void;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2" role="group" aria-label={`Quantity for ${label}`}>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        aria-label={`Remove one ${label}`}
        onClick={onDecrement}
      >
        -
      </Button>
      <span className="w-6 text-center" aria-live="polite">
        {value}
      </span>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        aria-label={`Add one ${label}`}
        onClick={onIncrement}
      >
        +
      </Button>
    </div>
  );
}

function CustomQuestionField({
  question,
  value,
  onChange,
}: {
  question: CustomQuestion;
  value: string;
  onChange: (value: string) => void;
}) {
  const fieldId = `question-${question.id}`;
  if (question.type === 'select') {
    return (
      <FormField label={question.label} htmlFor={fieldId} required={question.required}>
        <select
          id={fieldId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-brand border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">Select…</option>
          {(question.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </FormField>
    );
  }
  if (question.type === 'checkbox') {
    return (
      <div className="mb-4 flex items-center gap-2">
        <input
          id={fieldId}
          type="checkbox"
          checked={value === 'true'}
          onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
        />
        <label htmlFor={fieldId} className="text-sm text-slate-700">
          {question.label}
          {question.required && (
            <span aria-hidden="true" className="text-red-600">
              {' '}
              *
            </span>
          )}
        </label>
      </div>
    );
  }
  return (
    <FormField label={question.label} htmlFor={fieldId} required={question.required}>
      <Input
        id={fieldId}
        type={question.type === 'number' ? 'number' : 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </FormField>
  );
}
